
import TimeUnit from "./TimeUnit.js";
import ZonedClock from "./ZonedClock.js";
import DateUtils from "@/utils/DateUtils.js";
import RcDate from "./RcDate.js";
import RcMonth from "./RcMonth.js";
import RcYear from "./RcYear.js";
import RcHour from "./RcHour.js";
import RcQuarterYear from "./RcQuarterYear.js";

/** A day of the month, mirroring com.readychek.time.RcDay. */
export default class RcDay extends TimeUnit {

  constructor(date, timeZone) {
    super(date, timeZone);
  }

  /**
   * The day of the month, counted from zero: the 1st reads as 0. This matches the java
   * RcDay, which is what writes the stored timesheet day, so both clients agree on the
   * number they persist. Add one when showing it to a person.
   */
  value() {
    return this.dayOfMonth() - 1;
  }

  /** The day of the week, 0 for Sunday. */
  name() {
    return this.dayOfWeek();
  }

  date() {
    return new RcDate(this.internalDate(), this.timeZone());
  }

  day() {
    return this;
  }

  month() {
    return new RcMonth(this.internalDate(), this.timeZone());
  }

  year() {
    return new RcYear(this.internalDate(), this.timeZone());
  }

  quarter() {
    return new RcQuarterYear(this.internalDate(), this.timeZone());
  }

  /** Midnight at the start of this day. */
  start() {
    return new RcDate(
      ZonedClock.withFields(this.internalDate(), this.timeZone(),
        { hour: 0, minute: 0, second: 0, milli: 0 }),
      this.timeZone(),
    );
  }

  /** The last moment of this day, matching the java end() to the same millisecond. */
  end() {
    return new RcDate(
      ZonedClock.withFields(this.internalDate(), this.timeZone(),
        { hour: 23, minute: 59, second: 59, milli: 990 }),
      this.timeZone(),
    );
  }

  firstHour() {
    return new RcHour(this.start().internalDate(), this.timeZone());
  }

  lastHour() {
    return new RcHour(
      ZonedClock.withFields(this.internalDate(), this.timeZone(),
        { hour: 23, minute: 59, second: 59, milli: 999 }),
      this.timeZone(),
    );
  }

  yesterday() {
    return this.minus(1);
  }

  previous() {
    return this.yesterday();
  }

  tomorrow() {
    return this.plus(1);
  }

  next() {
    return this.tomorrow();
  }

  daysAgo(numberOfDays) {
    return this.minus(numberOfDays);
  }

  weeksAgo(numberOfWeeks) {
    return this.minus(numberOfWeeks * 7);
  }

  weeksAgo2() {
    return this.weeksAgo(2);
  }

  minus(days) {
    return new RcDay(new Date(this.time() - DateUtils.days(days)), this.timeZone());
  }

  plus(days) {
    return new RcDay(new Date(this.time() + DateUtils.days(days)), this.timeZone());
  }

  /** The gap to another day in whole days, reported without a sign. */
  daysFrom(other) {
    const gap = Math.abs(this.time() - other.time());
    return Math.floor(gap / DateUtils.days(1));
  }
}
